import { Link, useNavigate } from "react-router-dom";
import { HelpCircle, ArrowLeft } from "lucide-react";
import { CustomerFooter } from "@/components/CustomerFooter";
import { FAQChatBot } from "@/components/FAQChatBot";
import { setBypassBlocker } from "@/hooks/useNavigationBlocker";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ProfessionalFAQ = () => {
  const navigate = useNavigate();

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setBypassBlocker(true);
    navigate("/merchant-dashboard");
    setTimeout(() => setBypassBlocker(false), 100);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <a 
              href="/merchant-dashboard" 
              onClick={handleBackClick}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Dashboard</span>
            </a>
          </div>
        </div>
      </header>

      <main className="flex-1 container py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <HelpCircle className="w-8 h-8 text-cyan-600" />
              <h1 className="text-3xl font-bold text-foreground">Frequently Asked Questions</h1>
            </div>
            <p className="text-muted-foreground">
              Everything you need to know about Shakatah for Professionals
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="what-is" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                What is Shakatah for Professionals?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Shakatah for Professionals is a payment collection platform designed for service providers like coaches, consultants, therapists, tutors, and trainers. It helps you collect recurring payments from clients, send automatic reminders, and track all payments in one dashboard.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="how-works" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                How do I collect payments from clients?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <ol className="list-decimal list-inside space-y-2">
                  <li>Add your client's details (name, email, phone) to your client list.</li>
                  <li>Create a service or subscription with the payment amount and frequency.</li>
                  <li>Shakatah sends a personalized payment link to your client via email.</li>
                  <li>Your client pays securely, and you receive the funds directly.</li>
                </ol>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="pricing" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                What are the fees for professionals?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <p className="mb-3">We offer flexible pricing for professionals:</p>
                <ul className="list-disc list-inside space-y-2 mb-3">
                  <li><strong>3 months FREE</strong> — full access to get started</li>
                  <li><strong>Free Forever:</strong> 5 collections per month, always free</li>
                  <li><strong>Pay-per-use:</strong> ₹10 per collection (10¢ for USD)</li>
                  <li><strong>Starter:</strong> ₹500/month for 60 collections</li>
                  <li><strong>Growth:</strong> ₹1,000/month for 120 collections</li>
                </ul>
                <p className="text-sm italic">Annual billing available with 20% discount. Standard payment gateway charges apply.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="reminders" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                How do automatic reminders work?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Shakatah automatically sends payment reminders to your clients before and after the due date. You can customize reminder frequency and messaging from your dashboard settings. This helps reduce late payments without awkward follow-up conversations.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="recurring" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                Can I set up recurring payments?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Yes! You can create subscriptions with various frequencies: weekly, fortnightly, monthly, quarterly, or annual. Each payment period, a new payment link is automatically generated and sent to your client.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="payment-methods" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                What payment methods are supported?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <p className="mb-3">We support multiple payment options:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Razorpay</strong> — UPI, cards, netbanking (India)</li>
                  <li><strong>Stripe</strong> — Cards, Apple Pay, Google Pay (International)</li>
                </ul>
                <p className="mt-3 text-sm italic">Funds go directly to your account. We never hold your money.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="security" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                Is my payment information secure?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Absolutely! Shakatah never stores card details. All payments are processed through PCI-DSS compliant payment gateways (Razorpay/Stripe) with bank-grade encryption. Your clients' payment information is always secure.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="clients" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                How many clients can I manage?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                There's no limit on the number of clients you can add. Our dashboard is designed to scale with your practice, whether you have 5 clients or 500.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="terminology" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                Can I customize the terminology?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Yes! During onboarding, you can choose terminology that fits your profession. For example, therapists can use "Patients" instead of "Clients", or yoga instructors can call their service a "Studio" instead of "Practice". This customization appears throughout your dashboard.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="help" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                How do I get help?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                <p className="mb-3">We're here to help!</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Use the chat widget on any page (bottom-right corner)</li>
                  <li>Email us at <a href="mailto:support@shakatah.com" className="text-cyan-600 hover:underline">support@shakatah.com</a></li>
                  <li>Visit our <Link to="/contact" className="text-cyan-600 hover:underline">contact page</Link></li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </main>

      <CustomerFooter tagline="Simplifying professional payments" variant="professional" />
      <FAQChatBot context="merchant" />
    </div>
  );
};

export default ProfessionalFAQ;
